import { useState } from "react"

import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectInput,
  MultiSelectItem,
  MultiSelectList,
  MultiSelectTrigger,
  MultiSelectValue,
} from "@/components/motion/multi-select"

/** Creatable skills tag-input, composed on beUI's multi-select.
 *
 * The registry primitive has no catalog — options are whatever the candidate
 * types. Each non-empty, non-duplicate query renders a synthetic "Add …" row,
 * so Enter (or a click) commits the skill; the highlight falls back to that
 * row automatically. The cap is enforced here rather than in the primitive:
 * a rejected addition keeps the previous value, and onMaxSelected explains why.
 */
export default function SkillsPicker({
  values,
  onChange,
  max,
  onMaxSelected,
}: {
  values: string[]
  onChange: (next: string[]) => void
  max: number
  onMaxSelected?: () => void
}) {
  const [query, setQuery] = useState("")
  const trimmed = query.trim()
  const duplicate = values.some(
    (skill) => skill.toLowerCase() === trimmed.toLowerCase(),
  )
  const canCreate = trimmed.length > 0 && !duplicate

  const handleValueChange = (next: string[]) => {
    if (next.length > values.length && next.length > max) {
      onMaxSelected?.()
      return
    }
    onChange(next)
  }

  return (
    <MultiSelect value={values} onValueChange={handleValueChange} onQueryChange={setQuery}>
      <MultiSelectTrigger>
        <MultiSelectValue placeholder="Type a skill and press Enter…" />
        <MultiSelectInput
          aria-label="Add your skills"
          placeholder="Type a skill and press Enter…"
        />
      </MultiSelectTrigger>
      <MultiSelectContent>
        <MultiSelectList ariaLabel="Skills">
          {canCreate ? (
            <MultiSelectItem value={trimmed}>Add “{trimmed}”</MultiSelectItem>
          ) : null}
        </MultiSelectList>
      </MultiSelectContent>
    </MultiSelect>
  )
}
